import { reactive, unref, computed } from 'vue'
import { PLATFORM } from '@/common/js/constants.js'
import {
  LANDING_TYPE,
  BUDGET_MODE,
  DELIVERY_MODE,
  DELIVERY_TYPE,
  AD_TYPE,
  BID_TYPE,
  EXTERNAL_ACTION,
  DEEP_EXTERNAL_ACTION,
  APP_PROMOTION_TYPE,
  DEEP_BID_TYPE,
  BUDGET_OPTIMIZE_SWITCH,
  APP_TYPE
} from '../../consts/index.js'
import { isArray } from '@/common/js/utils.js'
import { getCaoHuaGames } from '@/api/common.model'
import { useProjectParamStates } from '../../hooks/useCommonComputeds.js'

export const useMarketingSceneForm = (projectForm, formRef, { isV3 = false } = {}) => {
  const pageData = reactive({ gameList: [] })
  getCaoHuaGames().then((res) => {
    pageData.gameList = res
  })

  // 根据平台id筛选的游戏列表
  const gameListFilterdByPlatform = computed(() => {
    return pageData.gameList.filter((game) => game.ch_platform_id === unref(projectForm).ch_platform_id)
  })

  const {
    isReservation,
    isManual,
    isProcedural
  } = useProjectParamStates(projectForm)

  const methods = {
    onLandingTypeChange(landingType) {
      // v4.26.1 如果是小程序投放，且推广目的为应用app（微端）时，定向切换到安卓
      const { ch_platform_id } = projectForm.value
      if (ch_platform_id === PLATFORM.MINI_PROGRAM) {
        if (landingType === LANDING_TYPE.APP) {
          projectForm.value.app_type = APP_TYPE.APP_ANDROID
          // 开启微端推广目的时，关闭回调开关
          projectForm.value.callback_switch = 0
        }
        if (landingType === LANDING_TYPE.MICRO_GAME) {
          projectForm.value.app_type = APP_TYPE.NONE
          // 开启微信小程序推广目的时，开启回调开关
          projectForm.value.callback_switch = 1
        }
      }
    },
    // 子目标切换
    onAppPromotionTypeChange() {
      // 如果是预约投放，深度优化切换到[无]
      if (isReservation.value) {
        projectForm.value.delivery_setting.deep_bid_type = ''
      }
    },
    onAdTypeChange(type) {
      if (type === AD_TYPE.SEARCH) {
        // 切换到搜索广告时，关闭搜索快投
        projectForm.value.keywordSwitch = 0
        // 旧版搜索广告去除常规投放，默认切换到极速智投
        if (!unref(isV3)) {
          projectForm.value.delivery_type = DELIVERY_TYPE.DURATION
        }
        // 搜索广告时不支持优化成本，切换到CUSTOM 稳定成本
        projectForm.value.delivery_setting.bid_type = BID_TYPE.CUSTOM
      }
    },
    onDeliveryModeChange(mode) {
      projectForm.value.delivery_setting.budget_mode = BUDGET_MODE.DAY

      // 子目的只支持手动投放
      if (mode === DELIVERY_MODE.PROCEDURAL) {
        projectForm.value.app_promotion_type = APP_PROMOTION_TYPE.DOWNLOAD
      }

      // 当delivery_mode = PROCEDURAL && landing_type = APP/MICRO_GAME时不支持UPPER_CONTROL；
      // 切换到CUSTOM 稳定成本
      const { bid_type } = projectForm.value.delivery_setting
      if (bid_type === BID_TYPE.UPPER_CONTROL) {
        projectForm.value.delivery_setting.bid_type = BID_TYPE.CUSTOM
      }

      formRef?.value?.clearValidate()
    },

    onDeliveryTypeChange(type) {
      if (type === DELIVERY_TYPE.DURATION) {
        projectForm.value.delivery_setting.bid_type = BID_TYPE.CUSTOM
      }
    }
  }

  return {
    isManual,
    isProcedural,
    isReservation,
    gameListFilterdByPlatform,
    ...methods
  }
}

export const useProjectForm = (projectForm, formRef) => {
  const isManual = computed(() => projectForm.value?.delivery_mode === DELIVERY_MODE.MANUAL)

  // 当bid_type=NO_BID且BUDGET=BUDGET_MODE_DAY时,“预算择优分配”必填;
  const budgetOptimizeSwitchVisible = computed(() => {
    const { budget_mode, bid_type } = projectForm.value.delivery_setting
    return isManual.value && bid_type === BID_TYPE.NO_BID && budget_mode === BUDGET_MODE.DAY
  })

  // 是否显示深度优化目标
  // 深度优化方式为ROI系数时显示付费ROI、变现ROI、变现ROI-7日（激活）、付费ROI-30日（付费）
  // 深度优化方式为首日ROI+7日ROI、每次付费+7日ROI时付费ROI-7日
  const deepExternalActionVisible = computed(() => {
    const { deep_bid_type, external_action } = projectForm.value.delivery_setting.optimize_goal
    const visible = {
      purchaseRoi: false,
      purchaseRoiSevenDay: false,
      monetizationRoi: false,
      monetizationRoiSevenDay: false,
      purchaseRoiThirtyDay: false
    }

    switch (deep_bid_type) {
      case DEEP_BID_TYPE.ROI_COEFFICIENT:
        visible.purchaseRoi = true
        // 浅层激活：可选变现ROI、变现ROI-7日
        if (external_action === EXTERNAL_ACTION.ACTIVE) {
          visible.monetizationRoi = true
          visible.monetizationRoiSevenDay = true
        }
        // 浅层付费：可选付费ROI-30日
        if (external_action === EXTERNAL_ACTION.PAY) {
          visible.purchaseRoiThirtyDay = true
        }
        break
      case DEEP_BID_TYPE.FIRST_AND_SEVEN_PAY_ROI:
      case DEEP_BID_TYPE.PER_AND_SEVEN_PAY_ROI:
        visible.purchaseRoiSevenDay = true
        break
      default:
        break
    }
    return visible
  })

  // 深度优化方式
  // 当优化目标为付费时，显示 每次付费、ROI系数、首日ROI+7日ROI、每次付费+7日ROI
  // 当优化目标为激活时，显示 ROI系数
  const deepBidTypeVisible = computed(() => {
    const { optimize_goal } = projectForm.value.delivery_setting
    const { external_action } = optimize_goal

    const visible = {
      bidPerAction: false,
      roiCoefficient: false,
      firstDayRoiSevenDayRoi: false,
      bidPerActionSevenDayRoi: false
    }

    switch (external_action) {
      case EXTERNAL_ACTION.PAY:
        visible.bidPerAction = true
        visible.roiCoefficient = true
        visible.firstDayRoiSevenDayRoi = true
        visible.bidPerActionSevenDayRoi = true
        break
      case EXTERNAL_ACTION.ACTIVE:
        visible.roiCoefficient = true
        break
      default:
        break
    }

    return visible
  })

  const methods = {
    onPlatformChange(id) {
      // 切换平台时，清空已选游戏
      projectForm.value.game = {}
      // 切换到小程序，推广目的需要切换到小程序投放，否则默认推广应用
      projectForm.value.landing_type = id === PLATFORM.MINI_PROGRAM ? LANDING_TYPE.MICRO_GAME : LANDING_TYPE.APP
    },
    // 切换游戏
    onGameChange({ game_address, sdks }) {
      const { ch_platform_id } = projectForm.value
      projectForm.value.game_address = game_address
      projectForm.value.download_url = game_address
      projectForm.value.subscribe_url = game_address
      projectForm.value.callback_switch = ch_platform_id === PLATFORM.ANDROID && isArray(sdks) && sdks.includes('2002') ? 0 : 1
    },
    onBlueFlowPackageChange(row) {
      // 保存项目当前选择的蓝海流量包，用于广告信息蓝海流量关键词参数选择和提交
      projectForm.value.seleted_blue_flow_package = row
      formRef?.value?.clearValidate(['blue_flow_package'])
    },
    // 优化目标切换
    onExternalTypeChange() {
      // 每次切换，深度优化方式切换到【无】
      projectForm.value.delivery_setting.optimize_goal.deep_bid_type = DEEP_BID_TYPE.DEFAULT
    },

    // 深度优化方式切换
    onDeepBidTypeChange(e) {
      if ([DEEP_BID_TYPE.FIRST_AND_SEVEN_PAY_ROI, DEEP_BID_TYPE.PER_AND_SEVEN_PAY_ROI].includes(e)) {
        projectForm.value.delivery_setting.optimize_goal.deep_external_action = DEEP_EXTERNAL_ACTION.PURCHASE_ROI_7D

        // 竞价策略切换到稳定成本
        if (e === DEEP_BID_TYPE.FIRST_AND_SEVEN_PAY_ROI) {
          projectForm.value.delivery_setting.bid_type = BID_TYPE.CUSTOM
        }
      }
      if (e === DEEP_BID_TYPE.ROI_COEFFICIENT) {
        // ROI系数下：付费ROI/变现ROI/变现ROI-7日/付费ROI-30日 可选，不自动锁定为付费ROI
        const cur = projectForm.value.delivery_setting.optimize_goal.deep_external_action
        const roiGoals = [
          DEEP_EXTERNAL_ACTION.PURCHASE_ROI,
          DEEP_EXTERNAL_ACTION.LT_ROI,
          DEEP_EXTERNAL_ACTION.LTV_ROI_SEVEN,
          DEEP_EXTERNAL_ACTION.PURCHASE_ROI_THIRTY
        ]
        if (!roiGoals.includes(cur)) {
          projectForm.value.delivery_setting.optimize_goal.deep_external_action = DEEP_EXTERNAL_ACTION.PURCHASE_ROI
        }
      }
    },

    onBudgetModeChange(e) {
      // CBO开关切换
      if (e === BUDGET_MODE.DAY) {
        if (projectForm.value.delivery_setting.bid_type === BID_TYPE.NO_BID) {
          projectForm.value.delivery_setting.budget_optimize_switch = BUDGET_OPTIMIZE_SWITCH.ON
        }
      }
    },
    // 竞价策略切换
    onBidTypeChange(e) {
      // 切换竞价策略，先清空cbo开关，后续根据日语算再判断是否开启
      projectForm.value.delivery_setting.budget_optimize_switch = BUDGET_OPTIMIZE_SWITCH.OFF

      // 开启最大转化，清空搜索快投的出价系数
      if (e === BID_TYPE.NO_BID) {
        projectForm.value.search_bid_ratio = ''

        // CBO开关切换
        if (projectForm.value.delivery_setting.budget_mode === BUDGET_MODE.DAY) {
          projectForm.value.delivery_setting.budget_optimize_switch = BUDGET_OPTIMIZE_SWITCH.ON
        }
      }

      formRef?.value?.clearValidate()
    }
  }

  return [
    {
      budgetOptimizeSwitchVisible,
      deepBidTypeVisible,
      deepExternalActionVisible
    },
    methods
  ]
}
